import { useEffect, useState, useCallback } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { format, addDays, startOfWeek } from "date-fns";
import { tr } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Copy, Plus, Trash2, Users, Link2, MessageCircle, Save, CalendarPlus, Pencil, Check, X,
  Eye, EyeOff} from "lucide-react";
import { DateInputTR, TimeInputTR } from "@/components/ui/date-picker-tr";
import { OpenClassDeleteDialog } from "@/components/OpenClassDeleteDialog";

const ymd = (d) => format(d, "yyyy-MM-dd");
const req = async (url, method = "GET", body) => {
  const res = await fetch(url, { method, credentials: "include", headers: body ? { "Content-Type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "İşlem başarısız");
  return data;
};
const Sel = ({ value, onChange, children, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 rounded-xl border border-input bg-white px-3 text-sm" data-testid={testId}>{children}</select>
);

const capFor = (room, branch) => Number(room?.branch_capacities?.[branch]) || Number(room?.capacity) || 1;

const CreateForm = ({ businessId, trainers, rooms, branches, prefill, onCreated, onDateChange }) => {
  const init = () => ({
    date: prefill?.date || ymd(new Date()), start_time: prefill?.start_time || "19:00", end_time: prefill?.end_time || "19:50",
    trainer_id: prefill?.trainer_id || trainers[0]?.trainer_id || "", ...initRoomBranch(),
  });
  const soleRoom = (b) => { const list = rooms.filter((r) => r.branches?.includes(b)); return list.length === 1 ? list[0] : null; };
  const initRoomBranch = () => {
    const pre = rooms.find((r) => r.room_id === prefill?.room_id);
    const branch = (pre && branches.find((b) => pre.branches?.includes(b))) || branches[0] || "";
    const room = pre?.branches?.includes(branch) ? pre : soleRoom(branch);
    return { branch, room_id: room?.room_id || "", capacity: room ? String(capFor(room, branch)) : "" };
  };
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(init()); }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onDateChange?.(f.date); }, [f.date]); // eslint-disable-line react-hooks/exhaustive-deps
  const [durations, setDurations] = useState({});
  useEffect(() => {
    req(`${API}/businesses/${businessId}/packages`).then((pkgs) => setDurations(branchDurations(pkgs))).catch(() => {});
  }, [businessId]);
  useEffect(() => {
    const d = durations[f.branch];
    if (d && !prefill?.end_time) setF((p) => ({ ...p, end_time: addMin(p.start_time, d) }));
  }, [durations]); // eslint-disable-line react-hooks/exhaustive-deps
  const branchRooms = rooms.filter((r) => r.branches?.includes(f.branch));
  const room = branchRooms.find((r) => r.room_id === f.room_id);
  const maxCap = room ? capFor(room, f.branch) : 0;
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const setRoom = (id) => { const r = rooms.find((x) => x.room_id === id); setF({ ...f, room_id: id, capacity: r ? String(capFor(r, f.branch)) : "" }); };
  const setCap = (e) => { const v = e.target.value; setF({ ...f, capacity: v && maxCap && Number(v) > maxCap ? String(maxCap) : v }); };
  const endFor = (start, branch) => (durations[branch] ? addMin(start, durations[branch]) : shiftEnd(f.start_time, f.end_time, start));
  const setStart = (e) => setF({ ...f, start_time: e.target.value, end_time: endFor(e.target.value, f.branch) });
  const setBranch = (v) => {
    const r = soleRoom(v);
    setF({ ...f, branch: v, end_time: endFor(f.start_time, v), room_id: r ? r.room_id : "", capacity: r ? String(capFor(r, v)) : "" });
  };

  const submit = async () => {
    if (!room) { toast.error("Lütfen seçtiğiniz branşın tanımlı olduğu bir oda seçin"); return; }
    const cap = Number(f.capacity || maxCap);
    if (cap < 1 || cap > maxCap) { toast.error(`Kontenjan 1 ile ${maxCap} arasında olmalı`); return; }
    setBusy(true);
    try {
      await req(`${API}/businesses/${businessId}/open-classes`, "POST", { ...f, capacity: cap });
      toast.success("Ders oluşturuldu");
      onCreated(f.date);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 [&>div]:min-w-0" data-testid="open-class-create-form">
      <div><Label>Tarih</Label><DateInputTR value={f.date} onChange={set("date")} minDate={new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-xl w-full min-w-0 max-w-full appearance-none" data-testid="open-class-date" /></div>
      <div><Label>Başlangıç</Label><TimeInputTR value={f.start_time} onChange={setStart} className="rounded-xl w-full min-w-0 max-w-full appearance-none" data-testid="open-class-start" /></div>
      <div><Label>Bitiş</Label><TimeInputTR value={f.end_time} onChange={set("end_time")} className="rounded-xl w-full min-w-0 max-w-full appearance-none" data-testid="open-class-end" /></div>
      <div><Label>Kontenjan</Label><Input type="number" min="1" max={maxCap || undefined} placeholder={maxCap ? `Maks. ${maxCap}` : "Önce oda seçin"} value={f.capacity} onChange={setCap} disabled={!room} className="rounded-xl" data-testid="open-class-capacity" /></div>
      <div><Label>Antrenör</Label><Sel value={f.trainer_id} onChange={set("trainer_id")} testId="open-class-trainer">{trainers.map((t) => <option key={t.trainer_id} value={t.trainer_id}>{t.name} {t.surname}</option>)}</Sel></div>
      <div><Label>Branş</Label><Sel value={f.branch} onChange={setBranch} testId="open-class-branch">{branches.map((b) => <option key={b} value={b}>{b}</option>)}</Sel></div>
      <div><Label>Oda</Label><Sel value={room ? f.room_id : ""} onChange={setRoom} testId="open-class-room"><option value="" disabled>{branchRooms.length ? "Oda seçin" : "Bu branş için oda yok"}</option>{branchRooms.map((r) => <option key={r.room_id} value={r.room_id}>{r.name} ({capFor(r, f.branch)} kişi)</option>)}</Sel></div>
      <div className="flex items-end"><Button onClick={submit} disabled={busy} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="open-class-create-submit"><Plus className="w-4 h-4 mr-1" /> Oluştur</Button></div>
    </div>
  );
};

const ShareLink = ({ businessId, open }) => {
  const [link, setLink] = useState("");
  useEffect(() => {
    if (!open) return;
    req(`${API}/businesses/${businessId}/public-booking/settings`)
      .then((d) => setLink(d.enabled && d.token ? `${window.location.origin}/rezervasyon/${d.token}` : ""))
      .catch(() => setLink(""));
  }, [open, businessId]);
  if (!link) return null;
  const text = `Derslerimize buradan yer ayırtabilirsiniz: ${link}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = link; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    toast.success("Link kopyalandı");
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 p-2" data-testid="open-classes-share">
      <Link2 className="w-4 h-4 text-emerald-700 shrink-0" />
      <span className="text-xs text-emerald-900 truncate flex-1 min-w-0" data-testid="open-classes-share-link">{link}</span>
      <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={copy} data-testid="open-classes-share-copy"><Copy className="w-4 h-4 mr-1" />Kopyala</Button>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-sm px-3 h-8 hover:bg-emerald-700" data-testid="open-classes-share-whatsapp">
        <MessageCircle className="w-4 h-4" />WhatsApp'ta Paylaş
      </a>
    </div>
  );
};

const WaitlistBlock = ({ c, onChanged }) => {
  const [removingId, setRemovingId] = useState(null);
  const [removedIds, setRemovedIds] = useState([]); // Anında "Çıkartıldı" durumuna geçenler

  const setStatus = async (id, status) => {
    try { 
      await req(`${API}/open-classes/waitlist/${id}`, "PUT", { status }); 
      onChanged(); 
    } catch (e) { 
      toast.error(e.message); 
    }
  };

  const handleRemove = async (waitlistId) => {
    if (removingId === waitlistId || removedIds.includes(waitlistId)) return;

    setRemovingId(waitlistId);
    try {
      await req(`${API}/open-classes/waitlist/${waitlistId}`, "DELETE");
      toast.success("Kişi bekleme listesinden çıkarıldı");
      
      // Anında 'Çıkartıldı' olarak işaretle (liste sunucudan gelene kadar 'Çıkar'a geri dönmesin)
      setRemovedIds((prev) => [...prev, waitlistId]);
      
      if (onChanged) {
        await onChanged();
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="basis-full rounded-lg bg-amber-50 border border-amber-200 p-2 space-y-1" data-testid={`open-class-waitlist-${c.class_id}`}>
      <p className="text-xs font-semibold text-amber-800">Bekleme listesi ({c.waitlist.length})</p>
      {c.waitlist.map((w, i) => {
        const isRemoving = removingId === w.waitlist_id;
        const isRemoved = removedIds.includes(w.waitlist_id);

        return (
          <div key={w.waitlist_id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`waitlist-entry-${w.waitlist_id}`}>
            <span className={`font-medium ${isRemoved ? "line-through text-slate-400" : ""}`}>{i + 1}. {w.name}</span>
            <span className="text-grey">{w.phone}</span>
            {w.payment_label && <Badge variant="outline" className="text-[10px]" data-testid={`waitlist-payment-${w.waitlist_id}`}>{w.payment_label}</Badge>}
            {w.is_new_member && <Badge className="bg-sky-100 text-sky-700 text-[10px]">Yeni üye</Badge>}
            {w.status === "notified" && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Haber verildi</Badge>}
            
            {!isRemoved && (
              <a href={w.wa_link} target="_blank" rel="noreferrer" onClick={() => w.status === "waiting" && setStatus(w.waitlist_id, "notified")}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-2 py-0.5 hover:bg-emerald-700" data-testid={`waitlist-wa-${w.waitlist_id}`}>
                <MessageCircle className="w-3 h-3" /> WhatsApp
              </a>
            )}

            <button 
              type="button" 
              disabled={isRemoving || isRemoved}
              className={`font-semibold ml-auto transition-colors ${
                isRemoved 
                  ? "text-slate-400 cursor-not-allowed italic" 
                  : isRemoving 
                    ? "text-amber-600 opacity-60 cursor-not-allowed" 
                    : "text-red-600 hover:underline"
              }`} 
              onClick={() => handleRemove(w.waitlist_id)} 
              data-testid={`waitlist-remove-${w.waitlist_id}`}
            >
              {isRemoved ? "Çıkartıldı" : isRemoving ? "Çıkarılıyor..." : "Çıkar"}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const toMin = (t) => { const [hh, mm] = (t || "0:0").split(":").map(Number); return hh * 60 + mm; };
const toHHMM = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
const addMin = (start, d) => toHHMM(Math.min(toMin(start) + d, 23 * 60 + 55));
// Most common session duration per branch, taken from the business's single-branch packages
const branchDurations = (pkgs) => {
  const count = {};
  (Array.isArray(pkgs) ? pkgs : pkgs?.packages || []).forEach((p) => {
    const brs = p.branches?.length ? p.branches : p.branch ? [p.branch] : [];
    if (brs.length !== 1 || !p.session_duration) return;
    const c = (count[brs[0]] = count[brs[0]] || {});
    c[p.session_duration] = (c[p.session_duration] || 0) + 1;
  });
  return Object.fromEntries(Object.entries(count).map(([b, c]) => [b, Number(Object.entries(c).sort((a, z) => z[1] - a[1])[0][0])]));
};
const shiftEnd = (oldStart, oldEnd, newStart) => {
  const d = toMin(oldEnd) - toMin(oldStart);
  return toHHMM(Math.min(toMin(newStart) + (d > 0 ? d : 50), 23 * 60 + 55));
};

const TimeEdit = ({ c, onDone }) => {
  const [t, setT] = useState({ start: c.start_time, end: c.end_time });
  const [busy, setBusy] = useState(false);
  const [moveCount, setMoveCount] = useState(0);
  const save = async (moveBookings = false) => {
    setBusy(true);
    try {
      await req(`${API}/open-classes/${c.class_id}`, "PUT", { start_time: t.start, end_time: t.end, move_bookings: moveBookings });
      toast.success(moveBookings ? "Ders ve rezervasyonlar yeni saate taşındı" : "Ders saati güncellendi");
      setMoveCount(0); onDone(true);
    } catch (e) {
      if (e.message.startsWith("MOVE_CONFIRM:")) setMoveCount(Number(e.message.split(":")[1]) || 1);
      else toast.error(e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`open-class-time-edit-${c.class_id}`}>
      <Dialog open={moveCount > 0} onOpenChange={(o) => !o && setMoveCount(0)}>
        <DialogContent aria-describedby={undefined} className="max-w-sm rounded-2xl" data-testid="open-class-move-confirm">
          <DialogHeader><DialogTitle>Rezervasyonlar da taşınsın mı?</DialogTitle></DialogHeader>
          <p className="text-sm text-grey">Bu derste <b>{moveCount} rezervasyon</b> var. Ders {t.start}–{t.end} saatine taşınırsa bu rezervasyonlar da yeni saate taşınacak.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setMoveCount(0)} data-testid="open-class-move-cancel">Vazgeç</Button>
            <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => save(true)} data-testid="open-class-move-confirm-btn">Evet, birlikte taşı</Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="w-28"><TimeInputTR value={t.start} onChange={(e) => setT({ start: e.target.value, end: shiftEnd(t.start, t.end, e.target.value) })} className="h-9" data-testid={`open-class-edit-start-${c.class_id}`} /></div>
      <span className="text-grey">–</span>
      <div className="w-28"><TimeInputTR value={t.end} onChange={(e) => setT({ ...t, end: e.target.value })} className="h-9" data-testid={`open-class-edit-end-${c.class_id}`} /></div>
      <Button size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 h-9" disabled={busy} onClick={() => save(false)} data-testid={`open-class-edit-save-${c.class_id}`}><Check className="w-4 h-4" /></Button>
      <Button size="sm" variant="ghost" className="rounded-lg h-9" onClick={() => onDone(false)} data-testid={`open-class-edit-cancel-${c.class_id}`}><X className="w-4 h-4" /></Button>
    </div>
  );
};

const ClassRow = ({ c, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [cap, setCap] = useState(String(c.capacity));
  const [busyVisibility, setBusyVisibility] = useState(false);

  const saveCap = async () => {
    if (Number(cap) === c.capacity) return;
    try { await req(`${API}/open-classes/${c.class_id}`, "PUT", { capacity: Number(cap) }); toast.success("Kontenjan güncellendi"); onChanged(); }
    catch (e) { toast.error(e.message); setCap(String(c.capacity)); }
  };

  const toggleVisibility = async () => {
    if (busyVisibility) return;
    setBusyVisibility(true);
    try {
      const res = await req(`${API}/open-classes/${c.class_id}/toggle-visibility`, "PUT");
      toast.success(res.is_hidden ? "Ders üyelere gizlendi" : "Ders üyelere görünür yapıldı");
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyVisibility(false);
    }
  };

  const [confirmDel, setConfirmDel] = useState(false);
  const remove = async (force) => {
    return req(`${API}/open-classes/${c.class_id}${force ? "?force=true" : ""}`, "DELETE");
  };

  return (
    <div 
      className={`rounded-xl border p-3 flex flex-wrap items-center gap-3 transition-colors ${
        c.is_hidden ? "bg-amber-50/40 border-amber-300" : "bg-white border-slate-200"
      }`} 
      data-testid={`open-class-row-${c.class_id}`}
    >
      <div className="min-w-[120px]">
        {editing ? <TimeEdit c={c} onDone={(changed) => { setEditing(false); if (changed) onChanged(); }} /> : (
          <button type="button" onClick={() => setEditing(true)} className="group inline-flex items-center gap-1.5 font-bold text-navy hover:text-emerald-700 transition-colors" title="Saati değiştir" data-testid={`open-class-edit-time-${c.class_id}`}>
            {c.start_time}–{c.end_time}<Pencil className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" />
          </button>
        )}
        <p className="text-xs text-grey">{c.trainer_name} · {c.room_name}{c.branch ? ` · ${c.branch}` : ""}</p>
      </div>

      <Badge className={c.available > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"} data-testid={`open-class-fill-${c.class_id}`}>
        <Users className="w-3 h-3 mr-1" /> {c.booked}/{c.capacity}{c.available === 0 ? " · Dolu" : ""}
      </Badge>

      {/* GİZLİ DERS ROZETİ */}
      {c.is_hidden && (
        <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300 flex items-center gap-1">
          <EyeOff className="w-3 h-3" /> Üyelere Gizli
        </Badge>
      )}

      <div className="flex-1 text-xs text-slate-600 min-w-[160px]">
        {c.attendees.length ? c.attendees.map((a, i) => (
          <span key={i} className="inline-flex items-center mr-2">{a.member_name}{a.card_name ? ` (${a.card_name})` : a.payment_type === "package" ? " (Paket)" : ""}{a.via_link && <Link2 className="w-3 h-3 ml-0.5 text-emerald-600" />}
            {a.package_request && <span className="ml-1 text-[10px] font-semibold text-orange-700 bg-orange-100 rounded px-1" data-testid="attendee-package-request-badge">Paket yok</span>}
            {a.no_show_30d > 0 && <span className="ml-1 text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1" title="Son 30 günde gelmeme sayısı" data-testid="attendee-no-show-badge">⚠ {a.no_show_30d}× gelmedi</span>}
          </span>
        )) : <span className="text-grey">Henüz katılımcı yok</span>}
      </div>

      {c.waitlist?.length > 0 && <WaitlistBlock c={c} onChanged={onChanged} />}

      <div className="flex items-center gap-1">
        <Input type="number" min="1" value={cap} onChange={(e) => setCap(e.target.value)} onBlur={saveCap} className="w-16 h-8 rounded-lg text-sm" title="Kontenjan" data-testid={`open-class-cap-input-${c.class_id}`} />
        
        {/* DERSİ GİZLE / GÖSTER BUTONU */}
        <Button
          variant="ghost"
          size="icon"
          disabled={busyVisibility}
          className={`h-8 w-8 ${c.is_hidden ? "text-amber-600 bg-amber-100/60 hover:bg-amber-100" : "text-slate-400 hover:text-slate-700"}`}
          onClick={toggleVisibility}
          title={c.is_hidden ? "Ders gizli. Üyelere görünür yapmak için tıklayın." : "Ders görünür. Üyelerden gizlemek için tıklayın."}
          data-testid={`open-class-toggle-visibility-${c.class_id}`}
        >
          {c.is_hidden ? <EyeOff className="w-4 h-4 text-amber-600" /> : <Eye className="w-4 h-4" />}
        </Button>

        <Button variant="ghost" size="icon" className="text-red-600 h-8 w-8" onClick={() => setConfirmDel(true)} data-testid={`open-class-delete-${c.class_id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <OpenClassDeleteDialog c={c} open={confirmDel} onOpenChange={setConfirmDel} onDelete={remove} onDeleted={onChanged} />
    </div>
  );
};

export const OpenClassesManager = ({ open, onOpenChange, businessId, trainers = [], rooms = [], branches = [], prefill = null }) => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [classes, setClasses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState(null);
  const focusDate = showForm ? formDate : null;
  const onFormDate = (d) => {
    setFormDate(d);
    if (d) setWeekStart((w) => { const n = startOfWeek(new Date(`${d}T12:00:00`), { weekStartsOn: 1 }); return ymd(n) === ymd(w) ? w : n; });
  };

  // Modal kapandığında yeni ders formunu kapat ve sıfırla
  useEffect(() => {
    if (!open) {
      setShowForm(false);
      setFormDate(null);
    }
  }, [open]);
  
  useEffect(() => {
    if (open && prefill?.date) {
      setWeekStart(startOfWeek(new Date(`${prefill.date}T12:00:00`), { weekStartsOn: 1 }));
      setShowForm(true);
    }
  }, [open, prefill]);

  const load = useCallback(async () => {
    try { setClasses(await req(`${API}/businesses/${businessId}/open-classes?start_date=${ymd(weekStart)}&end_date=${ymd(addDays(weekStart, 6))}`)); }
    catch (e) { toast.error(e.message); }
  }, [businessId, weekStart]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const copyWeek = async () => {
    try {
      const r = await req(`${API}/businesses/${businessId}/open-classes/copy-week`, "POST", { source_week_start: ymd(weekStart), target_week_start: ymd(addDays(weekStart, 7)) });
      toast.success(`${r.created} ders sonraki haftaya kopyalandı${r.skipped ? `, ${r.skipped} çakışma atlandı` : ""}`);
    } catch (e) { toast.error(e.message); }
  };

  const [skipped, setSkipped] = useState([]);
  const saveTemplate = async () => {
    try {
      const r = await req(`${API}/businesses/${businessId}/open-class-template/from-week`, "POST", { week_start: ymd(weekStart) });
      toast.success(`Haftalık şablon kaydedildi (${r.count} ders)`);
    } catch (e) { toast.error(e.message); }
  };
  const applyTemplate = async () => {
    try {
      const r = await req(`${API}/businesses/${businessId}/open-class-template/apply`, "POST", { week_start: ymd(weekStart) });
      setSkipped(r.skipped);
      toast.success(`${r.created} ders şablondan açıldı${r.skipped.length ? `, ${r.skipped.length} atlandı` : ""}`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { setSkipped([]); }, [weekStart]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[calc(100vw-1.5rem)] sm:w-[96vw] max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-1 [&>*]:min-w-0 p-4 sm:p-6" data-testid="open-classes-manager">
        <DialogHeader><DialogTitle>Online Ders Programı</DialogTitle></DialogHeader>
        <p className="text-xs text-grey -mt-2">Burada açtığınız dersler herkese açık rezervasyon linkinde boş yer sayısıyla görünür. Linkten gelen rezervasyonlar takvime otomatik düşer.</p>
        <ShareLink businessId={businessId} open={open} />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-xl" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="open-classes-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-semibold text-navy text-sm" data-testid="open-classes-week-label">{format(weekStart, "d MMM", { locale: tr })} – {format(addDays(weekStart, 6), "d MMM yyyy", { locale: tr })}</span>
          <Button variant="outline" size="icon" className="rounded-xl" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="open-classes-next-week"><ChevronRight className="w-4 h-4" /></Button>
          <div className="flex-1" />
          {/* Şablon ve Kopyalama Butonları - Geçici olarak kaldırıldı
          <Button variant="outline" className="rounded-xl" onClick={saveTemplate} disabled={!classes.length} data-testid="open-classes-save-template"><Save className="w-4 h-4 mr-1" /> Şablon Olarak Kaydet</Button>
          <Button variant="outline" className="rounded-xl" onClick={applyTemplate} data-testid="open-classes-apply-template"><CalendarPlus className="w-4 h-4 mr-1" /> Şablonu Bu Haftaya Uygula</Button>
          <Button variant="outline" className="rounded-xl" onClick={copyWeek} disabled={!classes.length} data-testid="open-classes-copy-week"><Copy className="w-4 h-4 mr-1" /> Haftayı Sonraki Haftaya Kopyala</Button>
          */}
          <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowForm(!showForm)} data-testid="open-classes-toggle-form"><Plus className="w-4 h-4 mr-1" /> Yeni Ders</Button>
        </div>
        {skipped.length > 0 && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="template-skipped-list">
            <p className="font-semibold mb-1">Atlanan saatler ({skipped.length}):</p>
            {skipped.map((x, i) => <p key={i}>• {x.slot} — {x.reason}</p>)}
          </div>
        )}
        {showForm && <CreateForm businessId={businessId} trainers={trainers} rooms={rooms} branches={branches} prefill={prefill} onCreated={() => load()} onDateChange={onFormDate} />}
        {focusDate && (
          <div className="flex items-center justify-between rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 text-sm" data-testid="open-classes-day-filter">
            <span className="text-slate-600">Sadece <b className="text-navy">{format(new Date(`${focusDate}T12:00:00`), "d MMMM EEEE", { locale: tr })}</b> günündeki dersler gösteriliyor</span>
            <button type="button" className="text-emerald-700 font-semibold hover:underline" onClick={() => setShowForm(false)} data-testid="open-classes-show-week">Tüm haftayı göster</button>
          </div>
        )}
        <div className="space-y-4">
          {days.filter((d) => !focusDate || ymd(d) === focusDate).map((d) => {
            const list = classes.filter((c) => c.date === ymd(d));
            if (!list.length) return null;
            return (
              <div key={ymd(d)} className="space-y-2">
                <p className="text-sm font-semibold text-slate-600">{format(d, "EEEE, d MMMM", { locale: tr })}</p>
                {list.map((c) => <ClassRow key={`${c.class_id}-${c.capacity}-${c.booked}`} c={c} onChanged={load} />)}
              </div>
            );
          })}
          {focusDate && classes.length > 0 && !classes.some((c) => c.date === focusDate) && <p className="text-center text-grey text-sm py-8" data-testid="open-classes-day-empty">Bu gün için henüz ders yok.</p>}
          {!classes.length && <p className="text-center text-grey text-sm py-8" data-testid="open-classes-empty">{focusDate ? "Bu gün için henüz ders yok." : "Bu hafta online ders yok."}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
